import { Close, ErrorOutline } from "@mui/icons-material";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { hostname } from "../../utils";

/**
 * Convert plain text to HTML if it doesn't already contain HTML tags.
 * This handles AI-personalized content which is stored as plain text.
 */
const convertToHtml = (content: string): string => {
  if (!content) return content;

  // Check if content already contains HTML tags
  const htmlTagPattern = /<[a-zA-Z][^>]*>/;
  if (htmlTagPattern.test(content)) {
    // Content already has HTML, return as-is
    return content;
  }

  // Convert plain text to HTML: replace newlines with <br> tags
  return content.replace(/\n\n/g, "<br><br>").replace(/\n/g, "<br>");
};

export default ({
  open,
  onClose,
  execution,
  getToken,
  companyId,
}: {
  open: boolean;
  onClose: () => void;
  execution: any;
  getToken: () => Promise<string>;
  companyId: string;
}) => {
  const [preview, setPreview] = useState("");

  const loadPreview = async () => {
    const currentToken = await getToken();
    axios
      .get(
        `${hostname}/sequences/${execution.sequenceId}/executions/${execution.id}/preview`,
        {
          params: { companyId },
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      )
      .then((res) => {
        // Convert plain text to HTML for proper display
        setPreview(convertToHtml(res.data.preview));
      });
  };

  useEffect(() => {
    if (execution) {
      loadPreview();
    }
  }, [execution]);

  if (!execution) return <></>;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5">{execution.member.email}</Typography>
          <IconButton onClick={() => onClose()}>
            <Close />
          </IconButton>
        </Box>
        {execution.subject}
      </DialogTitle>
      <DialogContent>
        {execution.failed && (
          <Alert
            severity="error"
            icon={<ErrorOutline />}
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle2" gutterBottom>
              {execution.failureReason || "Execution Failed"}
            </Typography>
            {execution.failureMessage && (
              <Typography variant="body2">
                {execution.failureMessage}
              </Typography>
            )}
          </Alert>
        )}
        <Box dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview) }}></Box>
      </DialogContent>
    </Dialog>
  );
};
